/** Shared cart line identity keyed by productUuid and optional eventHash. */

import type { CheckoutOrderProductRequest } from "./checkoutTypes";
import {
  type CartItemForOrderApi,
  mapCartItemsToOrderProducts as mapCartLinesToOrderApiProducts,
} from "./orderApi";
import {
  cartLineMergeKey,
  type CartLineForExtraSync,
} from "./webstoreCartExtras";

export type CartLineIdentity = {
  productUuid: string;
  eventHash?: string;
  /** Confirmed order line id; preferred for display list keys. */
  uuid?: string;
  parentUuid?: string | null;
  cartLineSource?: "autoExtra";
  parentProductUuids?: string[];
  invalid?: boolean;
};

export function isCatalogCartLine(item: Pick<CartLineIdentity, "productUuid">): boolean {
  return typeof item.productUuid === "string" && item.productUuid.length > 0;
}

function cartLineForMergeKey(
  item: Pick<
    CartLineIdentity,
    | "productUuid"
    | "eventHash"
    | "cartLineSource"
    | "parentProductUuids"
    | "parentUuid"
    | "invalid"
  >
): CartLineForExtraSync {
  const parentProductUuids = [
    ...(item.parentProductUuids ?? []),
    item.parentUuid ?? undefined,
  ].filter(
    (uuid): uuid is string => typeof uuid === "string" && uuid.trim().length > 0
  );

  return {
    productUuid: item.productUuid,
    quantity: 0,
    eventHash: item.eventHash,
    cartLineSource: item.cartLineSource,
    parentProductUuids: parentProductUuids.length
      ? parentProductUuids
      : undefined,
    invalid: item.invalid,
  };
}

/** React list key aligned with cart merge identity (product, event, parent linkage). */
export function cartLineKey(item: CartLineIdentity): string {
  if (item.uuid) {
    return `line:${item.uuid}`;
  }
  const mergeKey = cartLineMergeKey(cartLineForMergeKey(item));
  const eventPart = item.eventHash ? `eh:${item.eventHash}` : "noevent";
  const parentsIdx = mergeKey.indexOf("|parents:");
  const parentSuffix = parentsIdx >= 0 ? mergeKey.slice(parentsIdx) : "";
  let key = `uuid:${item.productUuid}|${eventPart}${parentSuffix}`;
  if (item.invalid === true) {
    key += "|invalid";
  }
  return key;
}

function eventsMatch(
  a: Pick<CartLineIdentity, "eventHash">,
  b: Pick<CartLineIdentity, "eventHash">
): boolean {
  if (!a.eventHash && !b.eventHash) {
    return true;
  }
  if (a.eventHash && b.eventHash) {
    return a.eventHash === b.eventHash;
  }
  return false;
}

/** Same cart line for merge/qty updates. */
export function cartLinesMatch(a: CartLineIdentity, b: CartLineIdentity): boolean {
  return eventsMatch(a, b) && a.productUuid === b.productUuid;
}

export type CartLineMutationTarget = {
  eventRef?: string;
  productUuid: string;
};

/** Build a probe line for matching cart mutations against stored items. */
export function cartLineMutationTarget(
  productUuid: string,
  eventRef?: string,
): CartLineIdentity {
  return {
    productUuid,
    eventHash: eventRef,
  };
}

export function cartItemMatchesMutationTarget(
  item: CartLineIdentity,
  target: CartLineMutationTarget,
): boolean {
  return cartLinesMatch(
    item,
    cartLineMutationTarget(target.productUuid, target.eventRef),
  );
}

export type OrderProductPayload = CheckoutOrderProductRequest;

/** @see mapCartLinesToOrderApiProducts in orderApi.ts (webstore order payload). */
export function mapCartItemsToOrderProducts(
  items: Array<CartLineIdentity & CartItemForOrderApi>
): OrderProductPayload[] {
  return mapCartLinesToOrderApiProducts(items);
}
